'use client';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  DLG_BTN_SECONDARY,
  DLG_BTN_DANGER,
} from '@/components/ui/dialogButtons';

import { useBajaPerfil } from '@/components/hooks/useBajaPerfil';
import { logoutUser } from '@/components/utils/logoutUser';

const INNER_ROW =
  'border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 rounded-lg';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Nº de perfiles activos: cambia el texto entre "este perfil" y "tu cuenta" */
  totalPerfiles: number;
}

const DeleteProfileDialog = ({ open, onOpenChange, totalPerfiles }: Props) => {
  const navigate = useNavigate();
  const { desactivarPerfil, loading } = useBajaPerfil();
  const [confirmado, setConfirmado] = useState(false);

  // Con varios perfiles solo pierde este; con uno solo se queda sin acceso a
  // Bookoll, así que el mensaje no puede ser el mismo.
  const esUnicoPerfil = totalPerfiles <= 1;

  const handleOpenChange = (value: boolean) => {
    if (!value) setConfirmado(false);
    onOpenChange(value);
  };

  const handleConfirm = async () => {
    if (!confirmado || loading) return;

    const idPerfil = localStorage.getItem('id_perfil');

    if (!idPerfil) {
      toast.error('No hemos podido identificar el perfil. Vuelve a entrar e inténtalo de nuevo.');
      return;
    }

    const res = await desactivarPerfil(idPerfil);

    if (!res.ok) {
      toast.error(res.error);
      return;
    }

    handleOpenChange(false);

    if (res.eraUltimoPerfil) {
      toast.success('Tu perfil ha quedado desactivado.');
      await logoutUser();
      navigate('/');
      return;
    }

    toast.success('Perfil dado de baja.');
    navigate('/profiles', { replace: true });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="z-[60] p-0 gap-0 w-full sm:max-w-lg max-sm:top-auto max-sm:bottom-0 max-sm:translate-y-0 max-sm:rounded-b-none rounded-2xl max-sm:rounded-t-2xl overflow-hidden">
        <DialogHeader className="px-4 sm:px-6 pt-5 pb-3">
          <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900 dark:text-white">
            <span className="w-9 h-9 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
              <Trash2 className="w-4.5 h-4.5 text-red-500 dark:text-red-400" />
            </span>
            Dar de baja mi perfil
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 sm:px-6 pb-5 space-y-4">
          <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
            {esUnicoPerfil
              ? 'Tu perfil quedará desactivado y perderás el acceso a Bookoll.'
              : 'Este perfil quedará desactivado. El resto de tus perfiles no se ven afectados.'}
          </p>

          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li className="flex gap-2">
              <span className="text-gray-400">•</span>
              Dejarás de tener acceso a tus citas, bonos e historial.
            </li>
            <li className="flex gap-2">
              <span className="text-gray-400">•</span>
              Los negocios en los que reservaste conservan sus propios registros.
            </li>
            <li className="flex gap-2">
              <span className="text-gray-400">•</span>
              Si tienes alguna cita pendiente, cancélala antes con el negocio.
            </li>
          </ul>

          <div className="flex gap-3 rounded-lg border border-[#bfded7] bg-[#eef6f4] p-3 text-xs leading-relaxed text-[#1e6a5c] dark:border-[#288E7F]/30 dark:bg-[#288E7F]/10 dark:text-[#7fd0c0]">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              El perfil no se elimina: puedes reactivarlo cuando quieras desde
              tu pantalla de perfiles. Si prefieres que borremos tu cuenta por
              completo, escríbenos a info@bookoll.com.
            </span>
          </div>

          {/* El botón queda bloqueado hasta que el usuario confirma. */}
          <div className={`${INNER_ROW} flex items-center justify-between gap-4 p-4`}>
            <Label
              htmlFor="confirmar-baja-perfil"
              className="text-sm font-normal leading-relaxed text-gray-600 dark:text-gray-300 cursor-pointer"
            >
              Entiendo que perderé el acceso a este perfil.
            </Label>
            <Switch
              id="confirmar-baja-perfil"
              checked={confirmado}
              disabled={loading}
              onCheckedChange={setConfirmado}
            />
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="outline"
            disabled={loading}
            onClick={() => handleOpenChange(false)}
            className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
          >
            Cancelar
          </Button>

          <Button
            onClick={handleConfirm}
            disabled={!confirmado || loading}
            className={`w-full sm:flex-1 ${DLG_BTN_DANGER} disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            {loading ? 'Dando de baja...' : 'Dar de baja'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteProfileDialog;